<template>
  <div class="inquiry-detail-container">
    <!-- 页面头部 -->
    <div class="page-header">
      <h2 class="page-title">询价单详情</h2>
      <div class="header-actions">
        <el-button @click="goBack" icon="el-icon-arrow-left">返回</el-button>
        
        <!-- 操作按钮 -->
        <div class="action-buttons" v-if="!loading">
          <!-- 撤销按钮 - 状态为0（审核中）时显示 -->
          <el-button
            v-if="inquiryDetail.status === 0"
            type="danger"
            @click="cancelInquiry"
            :loading="cancelling"
          >
            撤销询价单
          </el-button>
          
          <!-- 修改提交按钮 - 状态为-1（审核失败）时显示 -->
          <el-button
            v-if="inquiryDetail.status === -1"
            type="primary"
            @click="editInquiry"
          >
            修改提交
          </el-button>
        </div>
      </div>
    </div>

    <!-- 加载状态 -->
    <div v-if="loading" class="loading-container" v-loading="loading" element-loading-text="加载中...">
      <div style="height: 200px;"></div>
    </div>

    <!-- 内容区域 -->
    <div v-else class="detail-content">
      <!-- 产品信息卡片 -->
      <el-card class="detail-card" shadow="hover">
        <div slot="header" class="card-header">
          <span class="card-title">产品信息</span>
          <el-tag 
            :type="getStatusType(inquiryDetail.status)" 
            size="medium"
          >
            {{ inquiryDetail.status_name }}
          </el-tag>
        </div>

        <div class="product-info">
          <!-- 产品名称 -->
          <div class="info-row">
            <span class="label">产品名称：</span>
            <span class="value">{{ inquiryDetail.product_name || '' }}</span>
          </div>

          <!-- 产品图片展示区域 -->
          <div class="info-row">
            <span class="label">产品图片：</span>
            <div class="product-images">
              <el-image
                v-for="(image, index) in getDisplayImages()"
                :key="index"
                class="product-image"
                :src="formatImageUrl(image)"
                fit="cover"
                :preview-src-list="getPreviewImages()"
                @error="onImageError"
              >
                <div slot="error" class="image-slot">
                  <i class="el-icon-picture-outline"></i>
                </div>
              </el-image>
            </div>
          </div>

          <!-- 产品分类 -->
          <div class="info-row">
            <span class="label">产品分类：</span>
            <span class="value">{{ inquiryDetail.category_name || '未分类' }}</span>
          </div>

          <!-- 预计需求数量 -->
          <div class="info-row">
            <span class="label">预计需求数量：</span>
            <span class="value">{{ inquiryDetail.quantity }}</span>
          </div>

          <!-- 需求有效期限 -->
          <div class="info-row">
            <span class="label">需求有效期限：</span>
            <span class="value">{{ inquiryDetail.validDate }}</span>
          </div>

          <!-- 期望到货日期 -->
          <div class="info-row">
            <span class="label">期望到货日期：</span>
            <span class="value">{{ formatDeliveryDate(inquiryDetail.deliveryStartDate) }}</span>
          </div>

          <!-- 需求提交日期 -->
          <div class="info-row">
            <span class="label">需求提交日期：</span>
            <span class="value">{{ inquiryDetail.publishDate }}</span>
          </div>

          <!-- 联系电话 -->
          <div class="info-row">
            <span class="label">联系电话：</span>
            <span class="value">{{ inquiryDetail.contactPhone }}</span>
          </div>

          <!-- 详细描述 -->
          <div class="info-row" v-if="inquiryDetail.description">
            <span class="label">详细描述：</span>
            <div class="value description">{{ inquiryDetail.description }}</div>
          </div>

          <!-- 拒绝原因 -->
          <div class="info-row" v-if="inquiryDetail.reject_reason && inquiryDetail.status === -1">
            <span class="label">拒绝原因：</span>
            <div class="value reject-reason">{{ inquiryDetail.reject_reason }}</div>
          </div>
        </div>
      </el-card>

      <!-- 报价列表 -->
      <el-card v-if="quoteList.length > 0" class="quote-card" shadow="hover">
        <div slot="header" class="card-header">
          <span class="card-title">报价列表</span>
          <el-badge :value="quoteList.length" class="quote-badge" type="primary">
            <span>共{{ quoteList.length }}个报价</span>
          </el-badge>
        </div>

        <div class="quote-list">
          <el-card
            v-for="(quote, index) in quoteList"
            :key="index"
            class="quote-item"
            shadow="never"
          >
            <div class="quote-content">
              <div class="supplier-info">
                <div class="supplier-header">
                  <span class="supplier-name">{{ quote.site_name }}</span>
                  <span class="quote-date">{{ quote.create_time }}</span>
                </div>
              </div>

              <div class="quote-price">
                <span class="price-label">报价信息：</span>
                <span class="price-value">{{ quote.price }}</span>
              </div>

              <div class="quote-details" v-if="quote.additional_info">
                <span class="quote-label">附加信息：</span>
                <div class="quote-desc">{{ quote.additional_info }}</div>
              </div>

              <div class="contact-info">
                <div class="contact-row">
                  <span class="contact-label">联系人：</span>
                  <span class="contact-name">{{ quote.contact_person }}</span>
                </div>
                <div class="contact-row">
                  <span class="contact-label">联系电话：</span>
                  <span class="contact-phone">{{ quote.contact_phone }}</span>
                </div>
              </div>
            </div>
          </el-card>
        </div>
      </el-card>
    </div>
  </div>
</template>

<script>
import { getInquiryDetail, cancelInquiry } from '@/api/inquiry'
import { tree } from '@/api/goods/goodscategory'

export default {
  name: 'InquiryDetail',
  data() {
    return {
      inquiryId: '',
      inquiryDetail: {
        inquiry_id: '',
        product_name: '',
        productImages: [],
        category_id: '',
        category_name: '',
        description: '',
        quantity: '',
        validDate: '',
        deliveryStartDate: '',
        contactPhone: '',
        status: 0,
        status_name: '',
        publishDate: '',
        reject_reason: ''
      },
      quoteList: [],
      categoryList: [],
      categoryMap: {},
      loading: false,
      cancelling: false
    }
  },
  
  created() {
    this.checkParams();
    this.getCategoryList();
    this.getInquiryDetailData();
  },
  
  methods: {
    // 检查路由参数
    checkParams() {
      const { inquiry_id } = this.$route.query;
      if (inquiry_id) {
        this.inquiryId = inquiry_id;
        console.log('询价单ID:', this.inquiryId);
      } else {
        this.$message.error('询价单ID不能为空');
        this.goBack();
      }
    },
    
    // 获取产品分类列表
    async getCategoryList() {
      try {
        const response = await tree();
        if (response.code === 0 && response.data) {
          this.categoryList = response.data;
          this.buildCategoryMap();
        }
      } catch (error) {
        console.error('获取分类列表失败:', error);
      }
    },
    
    // 构建分类映射表
    buildCategoryMap() {
      const buildMap = (categories) => {
        categories.forEach(category => {
          this.categoryMap[category.category_id] = category.category_name;
          if (category.children && category.children.length > 0) {
            buildMap(category.children);
          }
        });
      };
      buildMap(this.categoryList);
    },
    
    // 根据分类ID获取分类名称
    getCategoryName(categoryId) {
      return this.categoryMap[categoryId] || '未分类';
    },
    
    // 获取询价单详情
    async getInquiryDetailData() {
      if (!this.inquiryId) {
        console.error('询价单ID为空');
        return;
      }
      
      console.log('开始获取询价单详情，ID:', this.inquiryId);
      this.loading = true;
      
      try {
        const response = await getInquiryDetail({ id: this.inquiryId });
        console.log('询价单详情响应:', response);
        
        if (response.code === 0 && response.data) {
          this.fillDetailData(response.data);
        } else {
          console.error('获取详情失败，响应:', response);
          this.$message.error(response.message || '获取详情失败');
        }
      } catch (error) {
        console.error('获取询价单详情失败:', error);
        this.$message.error('获取详情失败');
      } finally {
        this.loading = false;
      }
    },
    
    // 填充详情数据
    fillDetailData(data) {
      console.log('开始填充详情数据:', data);
      
      // 映射接口返回的数据到页面显示字段
      this.inquiryDetail = {
        inquiry_id: data.id,
        inquiry_sn: data.inquiry_sn,
        product_name: data.product_name,
        productImages: data.product_images || [],
        category_id: data.category_id,
        category_name: this.getCategoryName(data.category_id),
        description: data.description,
        quantity: data.quantity,
        validDate: data.validity_date,
        deliveryStartDate: data.expected_arrival_date,
        contactPhone: data.contact_phone,
        status: data.status,
        status_name: data.status_name,
        publishDate: data.create_time ? data.create_time.split(' ')[0] : '',
        reject_reason: data.reject_reason || ''
      };
      
      // 设置报价列表
      this.quoteList = data.quotes || [];
      
      console.log('详情数据填充完成:', this.inquiryDetail);
      console.log('报价列表:', this.quoteList);
    },
    
    // 获取要显示的图片数组
    getDisplayImages() {
      if (this.inquiryDetail.productImages && this.inquiryDetail.productImages.length > 0) {
        return this.inquiryDetail.productImages;
      }
      return [];
    },
    
    // 获取预览图片数组
    getPreviewImages() {
      return this.getDisplayImages().map(image => this.formatImageUrl(image));
    },
    
    // 格式化图片URL
    formatImageUrl(image) {
      if (!image) {
        return '';
      }
      // 如果是完整的URL（以http开头），直接返回
      if (image.startsWith('http')) {
        return image;
      }
      // 使用全局图片处理方法
      return this.$img(image);
    },
    
    // 图片加载失败处理
    onImageError(e) {
      console.log('图片加载失败:', e);
    },
    
    // 获取状态类型
    getStatusType(status) {
      switch (status) {
        case 0: return 'warning'; // 审核中
        case 1: return 'success'; // 报价中
        case 2: return 'info';    // 已完成
        case -1: return 'danger'; // 审核失败
        case -2: return 'info';   // 撤销询价
        default: return 'info';
      }
    },
    
    // 撤销询价单
    cancelInquiry() {
      // 先判断当前询价单的状态是否为0（审核中）
      if (this.inquiryDetail.status !== 0) {
        this.$message.error('当前状态不支持撤销');
        return;
      }
      
      this.$confirm('确定要撤销这个询价单吗？', '确认撤销', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        this.cancelling = true;
        try {
          const response = await cancelInquiry({ id: this.inquiryId });
          if (response.code === 0) {
            this.$message.success('操作成功');
            // 刷新详情数据
            setTimeout(() => {
              this.getInquiryDetailData();
            }, 1000);
          } else {
            this.$message.error(response.message || '操作失败');
          }
        } catch (error) {
          console.error('撤销询价单失败:', error);
          this.$message.error('网络错误，请重试');
        } finally {
          this.cancelling = false;
        }
      }).catch(() => {
        // 用户取消操作
      });
    },
    
    // 修改询价单
    editInquiry() {
      this.$router.push({
        path: '/member/inquiry_submit',
        query: {
          inquiry_id: this.inquiryId,
          mode: 'edit'
        }
      });
    },
    
    // 拨打供应商电话
    callSupplier(phone) {
      if (!phone) {
        this.$message.error('电话号码为空');
        return;
      }
      
      // 在PC端，我们可以尝试使用tel协议
      window.location.href = `tel:${phone}`;
    },
    
    // 格式化期望到货日期
    formatDeliveryDate(dateStr) {
      if (!dateStr) {
        return '';
      }
      
      // 如果包含逗号，说明是日期范围格式
      if (dateStr.includes(',')) {
        const dates = dateStr.split(',');
        if (dates.length === 2) {
          const startDate = dates[0].trim();
          const endDate = dates[1].trim();
          return `${startDate} 至 ${endDate}`;
        }
      }
      
      // 如果不是范围格式，直接返回原值
      return dateStr;
    },
    
    // 返回上一页
    goBack() {
      this.$router.go(-1);
    }
  }
}
</script>

<style lang="scss" scoped>
.inquiry-detail-container {
  padding: 20px;
  background-color: #f5f5f5;
  min-height: calc(100vh - 60px);
}

.page-header {
  background: #fff;
  padding: 20px;
  margin-bottom: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  justify-content: space-between;
  
  .page-title {
    margin: 0;
    font-size: 24px;
    font-weight: 600;
    color: #333;
  }
  
  .header-actions {
    display: flex;
    align-items: center;
    gap: 12px;
    
    .action-buttons {
      display: flex;
      align-items: center;
      gap: 12px;
    }
  }
}

.loading-container {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

// 移除底部操作按钮留出的空间，因为按钮已移至头部
.detail-content {
  margin-bottom: 20px;
}

.detail-card,
.quote-card {
  margin-bottom: 20px;
  
  .card-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    
    .card-title {
      font-size: 18px;
      font-weight: 600;
      color: #333;
    }
    
    .quote-badge {
      font-size: 14px;
      color: #666;
    }
  }
}

.product-info {
  .info-row {
    display: flex;
    align-items: flex-start;
    margin-bottom: 16px;
    
    &:last-child {
      margin-bottom: 0;
    }
    
    .label {
      flex-shrink: 0;
      width: 120px;
      font-weight: 500;
      color: #666;
      line-height: 1.5;
    }
    
    .value {
      flex: 1;
      color: #333;
      line-height: 1.5;
      
      &.description,
      &.reject-reason {
        white-space: pre-wrap;
        word-break: break-word;
      }
      
      &.reject-reason {
        color: #f56c6c;
        background: #fef0f0;
        padding: 8px 12px;
        border-radius: 4px;
        border-left: 4px solid #f56c6c;
      }
    }
  }
  
  .product-images {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    
    .product-image {
      width: 100px;
      height: 100px;
      border-radius: 8px;
      border: 1px solid #e8e8e8;
      cursor: pointer;
      
      .image-slot {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        height: 100%;
        background: #f5f5f5;
        color: #ccc;
        font-size: 24px;
      }
    }
  }
}

.quote-list {
  .quote-item {
    margin-bottom: 16px;
    border: 1px solid #e8e8e8;
    
    &:last-child {
      margin-bottom: 0;
    }
    
    .quote-content {
      .supplier-info {
        margin-bottom: 16px;
        
        .supplier-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          
          .supplier-name {
            font-size: 16px;
            font-weight: 600;
            color: #333;
          }
          
          .quote-date {
            font-size: 12px;
            color: #999;
          }
        }
      }
      
      .quote-price {
        display: flex;
        align-items: center;
        margin-bottom: 12px;
        
        .price-label {
          font-weight: 500;
          color: #666;
          margin-right: 8px;
        }
        
        .price-value {
          font-size: 16px;
          color: #ff6b35;
          font-weight: 600;
        }
      }
      
      .quote-details {
        display: flex;
        align-items: center;
        margin-bottom: 16px;
        
        .quote-label {
          font-weight: 500;
          color: #666;
          margin-right: 8px;
        }
        
        .quote-desc {
          color: #333;
          margin-top: 4px;
          white-space: pre-wrap;
          word-break: break-word;
        }
      }
      
      .contact-info {
        background-color: #f9f9f9;
        border-radius: 8px;
        padding: 16px;
        
        .contact-row {
          display: flex;
          align-items: center;
          margin-bottom: 8px;
          
          &:last-child {
            margin-bottom: 0;
          }
          
          .contact-label {
            font-weight: 500;
            color: #666;
            margin-right: 8px;
            flex-shrink: 0;
          }
          
          .contact-name,
          .contact-phone {
            color: #333;
            flex: 1;
          }
          
          .el-button {
            margin-left: 12px;
          }
        }
      }
    }
  }
}

// 响应式设计
@media (max-width: 768px) {
  .inquiry-detail-container {
    padding: 10px;
  }
  
  .page-header {
    padding: 15px;
    flex-direction: column;
    gap: 10px;
    
    .page-title {
      font-size: 20px;
    }
  }
  
  .product-info {
    .info-row {
      flex-direction: column;
      align-items: flex-start;
      
      .label {
        width: auto;
        margin-bottom: 4px;
      }
    }
  }
  
  .page-header {
    .header-actions {
      flex-direction: column;
      gap: 8px;
      
      .action-buttons {
        width: 100%;
        justify-content: center;
        
        .el-button {
          flex: 1;
          max-width: 150px;
        }
      }
    }
  }
}
</style>